import React from "react";
import dayjs from "dayjs";
import Table from "components/DynamicTable";
import Box from '@mui/material/Box';
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/styles";

import Chip from "components/Chip";
import { getStudentRecentInterviewScoreAndAttempts, getStudentInterviewAttempts } from "services/analyticsV2";
import { exportToCsv } from "utils";
import { IconButton as CustomIconButton } from "components/CustomButtons";
import FeedbackDialog from "components/FeedbackDialog";
import { Button, Typography } from "@mui/material";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { generateAttemptFeedbackUrl } from "services";
import { useSnackbar } from "contexts";
import WarningIcon from "@mui/icons-material/Warning";
import VerifiedIcon from "@mui/icons-material/Verified";
import MUITooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import CenterFlexBox from "components/CenterFlexBox";
import ImageProctorDialog from "dialogs/ImageProctorDialog";

const sx = {
  viewBtn: {
    fontSize: '10px',
    padding: '2px 3px',
    color: '#000',
    border: '1px solid #E1E2E3',
    textTransform: 'capitalize',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#02569D',
      color: '#fff'
    }
  }
}

const formatNumberOrDash = (value, decimals = 0) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return decimals > 0 ? num.toFixed(decimals) : num.toFixed(0);
};

const CustomTooltip = styled(({ className, ...props }) => (
  <MUITooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${MUITooltip.tooltipClasses?.tooltip || 'MuiTooltip-tooltip'}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

const ShowProctoringResult = ({
  proctoringRecords = [],
  onViewCapturedImage,
}) => {

  let hasViolation = false;
  let moveAwayCount = 0;
  let tabSwitchCount = 0;
  let hasImageProctor = false;
  let cameraDisabled = 0;
  let exitFullScreenCount = 0;

  if (proctoringRecords.length) {
    proctoringRecords.forEach((pr) => {
      if (pr.type === "CAMERA_DISABLED") {
        cameraDisabled++;
      }
      if (pr.type === "IMAGE_PROCTOR") {
        hasImageProctor = true;
      }
      if (pr.type === "GENERAL_PROCTOR") {
        if (pr.moveAwayCount > 0 || pr.tabSwitchCount > 0) {
          hasViolation = true;
          moveAwayCount = pr.moveAwayCount;
          tabSwitchCount = pr.tabSwitchCount;
        }
      }
      if(pr.type === "MOVE_AWAY") {
        hasViolation=true;
        moveAwayCount++;
      }
      if(pr.type === "TAB_SWITCH") {
        hasViolation=true;
        tabSwitchCount++;
      }
      if(pr.type === "EXIT_FULL_SCREEN") {
        hasViolation=true;
        exitFullScreenCount++;
      }
    });
  }

  const handleViewCapturedImage = () => {
    onViewCapturedImage(proctoringRecords);
  };

  return hasViolation ? (
    <CustomTooltip
      placement="right"
      title={
        <React.Fragment>
          <CenterFlexBox justifyContent="flex-start">
            <WarningIcon color="error" /> &nbsp;&nbsp;
            <Typography variant="body1"> Violation Found </Typography>
          </CenterFlexBox>
          <br />

          <ul style={{ padding: 0, margin: 0, marginLeft: 8 }}>
            {moveAwayCount !== 0 && (
              <li style={{ marginBottom: 4 }}>
                {moveAwayCount} times move away from the test screen.
              </li>
            )}
            {tabSwitchCount !== 0 && (
              <li>{tabSwitchCount} times switch between the tabs.</li>
            )}
            {cameraDisabled !== 0 && (
              <li>{cameraDisabled} times disabled the camera.</li>
            )}
            {exitFullScreenCount !== 0 && (
              <li>{exitFullScreenCount} times exited the full screen.</li>
            )}
          </ul>

          {hasImageProctor && (
            <>
              <br />
              <Button
                fullWidth
                size="small"
                variant="contained"
                onClick={handleViewCapturedImage}
                style={{ textTransform: "none" }}
              >
                View Captured image
              </Button>
            </>
          )}
        </React.Fragment>
      }
    >
      <WarningIcon color="error" />
    </CustomTooltip>
  ) : (
    <CustomTooltip
      placement="right"
      title={
        <React.Fragment>
          <CenterFlexBox justifyContent="flex-start">
            <VerifiedIcon color="success" /> &nbsp;&nbsp;
            <Typography variant="body1">No Violation Found </Typography>
          </CenterFlexBox>
          <br />
          <Typography variant="body2">
            User was not found with any violations in the assessment as per the
            guidelines.
          </Typography>

          {hasImageProctor && (
            <>
              <br />
              <Button
                fullWidth
                size="small"
                variant="contained"
                onClick={handleViewCapturedImage}
                style={{ textTransform: "none" }}
              >
                View Captured image
              </Button>
            </>
          )}
        </React.Fragment>
      }
    >
      <VerifiedIcon color="success" />
    </CustomTooltip>
  );
};

      
const statusChip = (status) => {  
  if (status === 'Live') return <Chip content="Live" bgColor='#DCFCE7' color='#00664A'/>;
  if (status === 'Expired') return <Chip content="Expired" bgColor='#FFE6E3' color='#C51407'/>;
  return <Chip label={status} size="small" />;
};

function RecentAttempts({ 
  interviewVariantType, 
  batchId, 
  activeTab, 
  analyticsSection,
  studentId,
  selectedMock
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const snackbar = useSnackbar();
  const [exporting, setExporting] = React.useState(false);
  const [feedbackUrl, setFeedbackUrl] = React.useState(null);
  const [capturedImages, setCapturedImages] = React.useState([]);
  const [openImageProctorDialog, setOpenImageProctorDialog] = React.useState(false);

  const verbalColumns = [
    {
      field: 'name',
      label: 'Next Round Mock',
      minWidth: 180,
      disableSorting: true,
      renderCell: row => {
        if (analyticsSection === 'Attempt') {   
          return row.row?.attemptNumber || `Attempt - ${row.row?._id?.slice(-2) || '1'}`;
        }
        return row.row?.name || row.row?.settings?.name || '-';
      }
    },
    {
      field: 'type',
      label: 'Type',
      minWidth: 180,
      disableSorting: true,
      align: 'center',
    },
    {
      field: 'proctor',
      label: 'Proctor',
      minWidth: 80,
      disableSorting: true,
      align: 'center',
      renderCell: row => (
        <ShowProctoringResult
          proctoringRecords={row.row?.proctoringRecords || []}
          onViewCapturedImage={handleViewCapturedImage}
        />
      ),
    },
    {
      field: 'status',
      label: 'Status',
      minWidth: 100,
      disableSorting: true,
      align: 'center',  
      renderCell: row => {
        const now = dayjs();
        const publish = row.row?.publishOn ? dayjs(row.row?.publishOn) : null;
        const expire = row.row?.expiresOn ? dayjs(row.row?.expiresOn) : null;
        if (publish && now.isBefore(publish)) return statusChip('Upcoming');
        if (row.row?.expiresOn && row.row?.expiresOn.startsWith('9999')) return statusChip('Live');
        if (expire && expire.isBefore(now)) return statusChip('Expired');
        return statusChip('Live');
      },
    },
    {
      field: 'publishOn',
      label: 'Published on',    
      minWidth: 120,
      disableSorting: true,
      renderCell: row => dayjs(row.row?.publishOn).format('DD/MM/YYYY'),
      align: 'center',
    },
    {
      field: 'attemptedOn',
      label: 'Attempted on',
      minWidth: 120,
      disableSorting: true,
      renderCell: row => {
        if (analyticsSection === 'Attempt') {
          return row.row?.finishedAt ? dayjs(row.row.finishedAt).format('DD/MM/YYYY') : 
                 row.row?.createdAt ? dayjs(row.row.createdAt).format('DD/MM/YYYY') : '-';
        }
        return row.updatedAt ? dayjs(row.updatedAt).format('DD/MM/YYYY') : '-';
      },
      align: 'center',
    },
    {
      field: 'finishedAttempts',
      label: 'Finished Attempts',
      minWidth: 100,
      disableSorting: true,
      renderCell: row => row.row?.finishedAttempts?.toString().padStart(1, '0') ?? '0',
      align: 'center',
    },
    {
      field: 'unfinishedAttempts',
      label: 'Unfinished Attempts',
      minWidth: 120,
      disableSorting: true,
      renderCell: row => row.row?.unfinishedAttempts?.toString().padStart(1, '0') ?? '0',
      align: 'center',
    },
    {
      field: 'overall',
      label: 'Overall score',
      minWidth: 100,
      disableSorting: true,
      renderCell: row => {
        if (analyticsSection === 'Attempt') {
          const comm = Number(row.row?.analysis?.communication?.ratings?.OVERALL);
          const cont = Number(row.row?.analysis?.content?.ratings?.OVERALL);
          if (!isNaN(comm) && !isNaN(cont)) return ((comm + cont) / 2).toFixed(0);
          return '-';
        }
        return formatNumberOrDash(row.row?.overallScore, 0);
      },
      align: 'center',
    },
    {
      field: 'overallContent',
      label: 'Content score',
      minWidth: 100,
      disableSorting: true,
      align: 'center',
      renderCell: row => {
        if (analyticsSection === 'Attempt') {
          const v = row.row?.analysis?.content?.ratings?.OVERALL;
          return formatNumberOrDash(v, 0);
        }
        return formatNumberOrDash(row.row?.overallContent, 0);
      }
    },
    {
      field: 'overallComm',
      label: 'Speech score',
      minWidth: 100,
      disableSorting: true,
      align: 'center',
      renderCell: row => {
        if (analyticsSection === 'Attempt') {
          const v = row.row?.analysis?.communication?.ratings?.OVERALL;
          return formatNumberOrDash(v, 0);
        }
        return formatNumberOrDash(row.row?.overallComm, 0);
      }
    },
  ];

  const codingColumns = [
    {
      field: 'challenge',
      label: 'Coding Challenge',
      minWidth: 220,
      disableSorting: true,
      renderCell: row => {
        if (analyticsSection === 'Attempt') {
          return row.row?.attemptNumber || `Attempt - ${row.row?._id?.slice(-2) || '1'}`;
        }
        return row?.row?.challenge || row?.row?.settings?.name || row?.row?.title || '-';
      },
    },
    {
      field: 'type',
      label: 'Type',
      minWidth: 140,
      disableSorting: true,
      align: 'center',
      renderCell: row => {
        if (analyticsSection === 'Attempt') {
          return row.row?.assessmentEntryPoint?.data || '-';
        }
        return row.row?.type || '-';
      },
    },
    {
      field: 'overallScore',
      label: 'Overall Score',
      minWidth: 120,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.overallScore, 1),
    },
    {
      field: 'coding',
      label: 'Coding',
      minWidth: 120,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.coding, 0),
    },
    {
      field: 'coreConcepts',
      label: 'Core Concepts',
      minWidth: 150,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.coreConcepts, 0),
    },
    {
      field: 'criticalThinking',
      label: 'Critical Thinking',
      minWidth: 160,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.criticalThinking, 0),
    },
    {
      field: 'content',
      label: 'Content',
      minWidth: 120,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.content, 0),
    },
    {
      field: 'speech',
      label: 'Speech',
      minWidth: 120,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.speech, 0),
    },
    {
      field: 'dsaUsageAndUnderstanding',
      label: 'DSA Usage & Understanding',
      minWidth: 200,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.dsaUsageAndUnderstanding, 0),
    },
    {
      field: 'edgeCaseHandling',
      label: 'Edge Case Handling',
      minWidth: 160,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.edgeCaseHandling, 0),
    },
    {
      field: 'codeOrganizationAndClarity',
      label: 'Code Organization & Clarity',
      minWidth: 200,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.codeOrganizationAndClarity, 0),
    },
    {
      field: 'scalabilityAndEfficiency',
      label: 'Scalability & Efficiency',
      minWidth: 180,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.scalabilityAndEfficiency, 0),
    },
    {
      field: 'conceptualClarity',
      label: 'Conceptual Clarity',
      minWidth: 160,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.conceptualClarity, 0),
    },
    {
      field: 'logicalReasoningAndAnalysis',
      label: 'Logical Reasoning & Analysis',
      minWidth: 220,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.logicalReasoningAndAnalysis, 0),
    },
    {
      field: 'adaptiveImprovement',
      label: 'Adaptive Improvement',
      minWidth: 180,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.adaptiveImprovement, 0),
    },
    {
      field: 'explanationAndLogicalCoherence',
      label: 'Explanation & Logical Coherence',
      minWidth: 240,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.explanationAndLogicalCoherence, 0),
    },
    ...(interviewVariantType === 'coding_iw' ? [
      {
        field: 'fluency',
        label: 'Fluency',
        minWidth: 120,
        disableSorting: true,
        align: 'center',
        renderCell: row => formatNumberOrDash(row.row?.fluency, 0),
      },
      {
        field: 'delivery',
        label: 'Delivery',
        minWidth: 120,
        disableSorting: true,
        align: 'center',
        renderCell: row => formatNumberOrDash(row.row?.delivery, 0),
      },
    ] : []),
    {
      field: 'finishedAttempts',
      label: 'Finished Attempts',
      minWidth: 140,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.finishedAttempts, 0),
    },
    {
      field: 'unfinishedAttempts',
      label: 'Unfinished Attempts',
      minWidth: 160,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.unfinishedAttempts, 0),
    },
    {
      field: 'averageTimeTaken',
      label: 'Avg. Time Taken (min)',
      minWidth: 160,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.averageTimeTaken, 1),
    },
    {
      field: 'averageViolationCount',
      label: 'Avg. Violations',
      minWidth: 140,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.averageViolationCount, 0),
    },
    {
      field: 'averageHintUsed',
      label: 'Avg. Hints Used',
      minWidth: 140,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.averageHintUsed, 0),
    },
    {
      field: 'uniqueChallengeAttempted',
      label: 'Unique Challenges Attempted',
      minWidth: 220,
      disableSorting: true,
      align: 'center',
      renderCell: row => formatNumberOrDash(row.row?.uniqueChallengeAttempted, 0),
    },
    {
      field: 'mostRecentAttemptAt',
      label: 'Most Recent Attempt',
      minWidth: 200,
      disableSorting: true,
      align: 'center',
      renderCell: row => {
        const v = row.row?.mostRecentAttemptAt;
        return v ? dayjs(v).format('DD-MM-YYYY HH:mm') : '-';
      },
    },
  ];

  const handleExport = async (selectedColumns) => {
    try {
      setExporting(true);
      const exportData = await getStudentRecentInterviewScoreAndAttempts({
        studentId: id,
        interviewVariantType,
        batch: activeTab === 'interviewWizard' ? null : batchId,
        type: 'all',
      });
      
      if (exportData) {
        let processedData;
        
        if (interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw') {
          const source = Array.isArray(exportData.performance) ? exportData.performance : [];
          processedData = source;
        } else {
          const source = Array.isArray(exportData.performance) ? exportData.performance : [];
          processedData = source;
        }
        const columnFields = selectedColumns.map(col => col.field || col.id);
        exportToCsv(selectedColumns, processedData, columnFields, 'recent-attempts');
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(false);
    }
  };

  const getVerbalColumnsWithConditionals = () => {
    let baseColumns = [...verbalColumns];
    
    if (analyticsSection === 'Attempt') {
      baseColumns = baseColumns.filter(column => 
        column.field !== 'finishedAttempts' && column.field !== 'unfinishedAttempts'
      );
    } else {
      baseColumns = baseColumns.filter(column => 
        column.field !== 'attemptedOn' && column.field !== 'type' && column.field !== 'proctor'
      );
    }
    
    if (analyticsSection !== 'Attempt' && activeTab !== 'interviewWizard') {
      baseColumns.push({
        id: "analytics",
        label: "Feedback", fixed: true,
        minWidth: 120, disableSorting: true,
        align: "center",
        renderCell: ({ row }) =>
          <CustomIconButton  
            variant='contained'
            onClick={() => {
              const interviewId = row._id;
              if (interviewVariantType === 'verbal_mock') {
                navigate(`/personalizedInterview/${interviewId}/performance/next-round`);
              } else if (interviewVariantType === 'coding_mock') {
                navigate(`/personalizedInterview/${interviewId}/performance/coding-round`);
              }
            }}
            style={{
                      border : `1px solid ${theme.palette.primary.main}`,
                      padding : theme.spacing(1),
                      marginLeft : theme.spacing(18)
                  }}
          >
            <img 
              src='https://assets.languify.in/images/PerformanceIcon.svg' 
              alt='performance' 
            />
          </CustomIconButton>,
      });
    }
    
    return baseColumns;
  };

  const getCodingColumnsWithConditionals = () => {
    let baseColumns = [...codingColumns];
    
    if (analyticsSection === 'Attempt') {
      baseColumns = baseColumns.filter(column => 
        column.field !== 'totalAttempts'
      );
    } else {
      baseColumns = baseColumns.filter(column => 
        column.field !== 'proctor'
      );
    }
    
    if (interviewVariantType === 'coding_iw' && analyticsSection === 'Interview') {
      baseColumns = baseColumns.filter(column => 
        column.field !== 'type' && column.field !== 'finishedAt'
      );
    }
    
    if (analyticsSection !== 'Attempt' && activeTab !== 'interviewWizard') {
      baseColumns.push(
        {
          id: "analytics",
          label: "Feedback", fixed: true,
          minWidth: 120, disableSorting: true,
          align: "center",
          renderCell: ({ row }) =>
            <CustomIconButton  
              variant='contained'
              onClick={() => {
                const interviewId = row._id;
                if (interviewVariantType === 'verbal_mock') {
                  navigate(`/personalizedInterview/${interviewId}/performance/next-round`);
                } else if (interviewVariantType === 'coding_mock') {
                  navigate(`/personalizedInterview/${interviewId}/performance/coding-round`);
                }
              }}
              style={{
                        border : `1px solid ${theme.palette.primary.main}`,
                        padding : theme.spacing(1),
                        marginLeft : theme.spacing(18)
                    }}
            >
              <img 
                src='https://assets.languify.in/images/PerformanceIcon.svg' 
                alt='performance' 
              />
            </CustomIconButton>,
        }
      );
    }
    
    return baseColumns;
  };

  const columns = interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw' 
    ? getCodingColumnsWithConditionals() 
    : getVerbalColumnsWithConditionals();

  const getApiAndFilters = () => {
    if (analyticsSection === 'Attempt') {
      return {
        getItems: async (params) => {
          const result = await getStudentInterviewAttempts(params);
          if (result?.data) {
            result.data = result.data.map((item, index) => ({
              ...item,
              attemptNumber: `Attempt - ${index + 1}`
            }));
          }
          return result;
        },
        initialFilters: {
          studentId: studentId || id,
          interviewVariantType,
          interviewId: selectedMock,
          pageSize: 10,
          pageNumber: 1,
          orderBy: 'title',
          order: 'asc',
        }
      };
    } else {
      return {
        getItems: async (filters) => {
          const res = await getStudentRecentInterviewScoreAndAttempts(filters);
          if (res && res.performance && res.total !== undefined) return res;
          return { performance: res?.data || [], total: res?.total || (res?.data?.length || 0) };
        },
        initialFilters: {
          studentId: studentId || id,
          interviewVariantType,
          batchId: activeTab === 'interviewWizard' ? null : batchId,
        }
      };
    }
  };

  const { getItems, initialFilters } = getApiAndFilters();

  const handelViewFeedback = async (id) => {
    const options = {};
    if (interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw') {
      options.coding = true;
    }
    const url = await generateAttemptFeedbackUrl(id, options);
    setFeedbackUrl(url);
  }

  const handleViewCapturedImage = (proctoringRecords) => {
    const imageRecords = proctoringRecords.filter(record => record.type === "IMAGE_PROCTOR");
    setCapturedImages(imageRecords);
    setOpenImageProctorDialog(true);
  };

  const getActions = [
    {
      title: "View Performance",
      renderIcon: () => <Button  sx={sx.viewBtn}>
        <RemoveRedEyeIcon style={{ padding: '1px', marginRight: '5px', fontSize: '15px' }} />
        View
      </Button>,
      label: "View Performance",
      onClick: (param) => {
        if (!param.row.finishedAt) {
          snackbar.error("The attempt was not completed");
          return;
        }
        handelViewFeedback(param.id);
      }
    }
  ];

  return (
    <Box sx={{marginTop : '24px'}}>
      <Table
        entity="InterviewAttempt"
        columns={columns}
        filters={[]}
        actions={analyticsSection === 'Attempt'  ? getActions : null}
        useListProps={{
          getItems,
          itemName: analyticsSection === 'Attempt' ? 'data' : 'performance',
          initialFilters,
        }}
        noRowsMessage="Seems this student haven't taken any assessment!!"
        height="none"
        autoHeight={true}
        pagination={false}
        autoPageSize={false}
        enableCsvExport={true}
        hideColumnSelection={false}
        onExport={handleExport}
      />
      <FeedbackDialog
        openDialog={feedbackUrl ? true : false}
        setOpenDialog={() => setFeedbackUrl(null)}
        url={feedbackUrl}
      />
      <ImageProctorDialog
        open={openImageProctorDialog}
        proctoringRecords={capturedImages}
        onClose={() => setOpenImageProctorDialog(false)}
      />
    </Box>
  );
}

export default RecentAttempts;
